import React, { createContext, useEffect, useMemo, useState } from 'react';
import { GetServerSideProps } from 'next';

import {
  Building,
  EntityWithId,
  Neighborhood,
  RequestUrl,
  StrapiBulkResponseData,
} from 'apolloClient/types';
import {
  flattenStrapiBulkDataItems,
  flattenStrapiDataItem,
} from 'lib/flattenStrapiBulkDataItems';
import { Blog, Blog as BlogType } from 'apolloClient/types/Blog';
import { client } from 'apolloClient/client';
import { queryWithRetry } from 'lib/queryWithRetry';
import { fetchRemainingPages } from 'lib/fetchRemainingPages';
import {
  getUnitPageQuery,
  GET_IS_LISTING_FAVS,
  NEARBY_CONDOS,
  NEARBY_NEIGHBOR_CONDOS,
  RequestPropertyType,
  GET_ALL_LISTINGS_FOR_UNIT_PAGE,
  UNIT_LISTINGS_PAGE_SIZE,
} from 'apolloClient/queries/unit';
import {
  Listing,
  ListingCollectionResponse,
  ListingPropertyType,
  Unit,
} from 'apolloClient/types/Units';
import { Entity, ResponseCollection } from 'apolloClient/types/common';
import { CalculatedNearbyCondosParams } from 'lib/nearbyCondosParams';
import { notfoundRedirect } from 'constants/notFoundParams';
import { ORIGIN } from 'src/constants';

import { City } from '../../../../apolloClient/types/City';

import UnitPage from '../../../../components/Unit/UnitPage';
import { useRouter } from 'next/router';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useAuth, useOnLoggedIn } from 'components/Auth/AuthProvider';
import { useFavorite } from 'components/SavedHouses/SavedFavoritesContext';
import { updateListingFavoritesQuery } from 'src/utils/updateListingFavoritesQuery';
import { FavoriteListingsResponse } from 'apolloClient/types/FavoriteListing';
import { getPropertyTypeByCode } from 'src/utils/getPropertyTypeByCode';
import { getNearbyCondosParamsUnit } from 'lib/nearbyCondosParamsUnit';
import { BuildingPageContextInterface } from 'apolloClient/types/Building';
import {
  buildRegistrationProperty,
  setRegistrationProperty,
} from 'src/utils/registrationProperty';

type UnitPageUnitResponse = {
  listings: ListingCollectionResponse;
  blogs?: ResponseCollection<Blog>;
};

interface UnitPageProps {
  listing: EntityWithId<Listing>;
  listings: EntityWithId<ListingPropertyType>[];
  blogs: BlogType[];
  neighborhood: Neighborhood;
  building: Building;
  unit: EntityWithId<Unit>;
  city: City;
  url: RequestUrl;
}

export interface UnitPageContextInterface {
  nearbyCondos: Entity<Listing>[];
  calculatedParams: CalculatedNearbyCondosParams;
  blogs: Blog[];
  listing: EntityWithId<Listing>;
  listings: EntityWithId<ListingPropertyType>[];
  building: Building;
  unit: EntityWithId<Unit>;
  neighborhood: Neighborhood;
  city: City;
  nearbyCondosTotal: number;
  url: RequestUrl;
  favorite?: FavoriteListingsResponse;
  nearbyCondosLoading?: boolean;
}

export const UnitPageContext = createContext<UnitPageContextInterface | null>(
  null
);

const UnitComponent: React.FC<UnitPageProps> = ({
  listing,
  listings,
  blogs,
  building,
  unit,
  neighborhood,
  city,
}) => {
  const { asPath } = useRouter();
  const { getActualAuthData } = useAuth();
  const { latestAction } = useFavorite();

  const [favorite, setFavorite] = useState(unit.favorite);

  const initialAuthData = getActualAuthData();

  // Feeds the FUB Registration event when a visitor signs up from this page —
  // the register popup renders outside this page's context (see _app).
  useEffect(() => {
    setRegistrationProperty(
      buildRegistrationProperty({
        listing,
        building,
        cityName: city?.name,
        url: `${ORIGIN}${asPath}`,
      })
    );

    return () => setRegistrationProperty(null);
  }, [listing, building, city, asPath]);

  // const buildingStats = building?.buildingStats;

  const nearbyCondosParams = getNearbyCondosParamsUnit({
    minAvgCoef: 0.8,
    maxAvgCoef: 1.2,
    sqft: listing.sqft,
    priceSqft: listing.priceSqft || 0,
  });

  const [
    fetchNearbyCondos,
    {
      data: nearbyCondosData,
      loading: nearbyCondosLoading,
      updateQuery: updateNearbyCondos,
      refetch: refetchNearbyCondos,
    },
  ] = useLazyQuery<{
    listings: ListingCollectionResponse;
    nearbyCondosLoading: boolean;
  }>(NEARBY_CONDOS, {
    variables: {
      ...nearbyCondosParams,
      propertyTypeList: [listing?.propertyTypeCode],
      listingId: listing?.id,
      neighborhoodId: neighborhood?.id,
      nearbyCondosLoading: true,
      includeFavorites: !!initialAuthData?.user?.id,
    },
  });

  const [fetchIsfavs, { data: favoritesData }] = useLazyQuery<
    {
      favoriteListings: {
        data: [
          {
            id: number;
          }
        ];
      };
    },
    {
      userId: number;
      listingId: number;
      type: string;
    }
  >(GET_IS_LISTING_FAVS);

  useEffect(() => {
    const authData = getActualAuthData();
    fetchNearbyCondos({
      variables: {
        ...nearbyCondosParams,
        propertyTypeList: [listing?.propertyTypeCode],
        listingId: listing?.id,
        neighborhoodId: neighborhood?.id,
        userId: authData.user.id || -1,
        includeFavorites: !!authData?.user?.id,
      },
    });
  }, []);

  const [
    fetchNearbyNeigborsCondos,
    {
      data: nearbyNeigborsCondos,
      updateQuery: updateNearbyNeigborsCondos,
      refetch: refetchNearbyNeigborsCondos,
    },
  ] = useLazyQuery<{
    listings: ListingCollectionResponse;
  }>(NEARBY_NEIGHBOR_CONDOS, {
    variables: {
      ...nearbyCondosParams,
      propertyTypeList: [listing?.propertyTypeCode],
      listingId: listing?.id,
      neighborhoodId: neighborhood?.id,
      includeFavorites: !!initialAuthData?.user?.id,
    },
  });

  useEffect(() => {
    if (!nearbyCondosData) return;
    if (nearbyCondosData && nearbyCondosData?.listings?.data?.length >= 3) {
      return;
    }

    const authData = getActualAuthData();
    fetchNearbyNeigborsCondos({
      variables: {
        ...nearbyCondosParams,
        propertyTypeList: [listing?.propertyTypeCode],
        listingId: listing?.id,
        neighborhoodId: neighborhood?.id,
        userId: authData.user.id || -1,
        includeFavorites: !!authData?.user?.id,
      },
    });
  }, [nearbyCondosData]);

  useEffect(() => {
    if (!favoritesData) return;
    const { favoriteListings } = favoritesData;
    if (!favoriteListings.data.length) {
      return setFavorite({ data: [] });
    }
    const favorite = favoriteListings.data[0];
    setFavorite({
      data: [
        {
          ...favorite,
          attributes: {
            type: getPropertyTypeByCode(listing.propertyTypeCode),
            unit: {
              data: {
                id: unit.id,
                attributes: { ...unit },
              },
            },
          },
        },
      ],
    });
  }, [favoritesData]);

  useOnLoggedIn(() => {
    const authData = getActualAuthData();
    if (nearbyCondosData?.listings?.data?.length) {
      refetchNearbyCondos({
        userId: authData.user.id || -1,
        includeFavorites: !!authData?.user?.id,
      });
    }
    if (nearbyNeigborsCondos?.listings?.data?.length) {
      refetchNearbyNeigborsCondos({
        userId: authData.user.id || -1,
        includeFavorites: !!authData?.user?.id,
      });
    }
    fetchIsfavs({
      variables: {
        listingId: listing.id,
        userId: authData.user.id || -1,
        type: getPropertyTypeByCode(listing.propertyTypeCode),
      },
    });
  });

  useEffect(() => {
    if (latestAction) {
      if (latestAction.unitId === unit.id) {
        return setFavorite({
          data:
            latestAction.action === 'create'
              ? [
                  {
                    id: latestAction.favoriteId,
                    attributes: {
                      type: getPropertyTypeByCode(listing.propertyTypeCode),
                      unit: {
                        data: {
                          attributes: { ...unit },
                          id: unit.id,
                        },
                      },
                    },
                  },
                ]
              : [],
        });
      }
      if (
        nearbyCondosData?.listings?.data?.some(
          ({
            id,
            attributes: {
              propertyTypeCode,
              unit: {
                data: { id: unitId },
              },
            },
          }) =>
            latestAction.unitId === unitId &&
            getPropertyTypeByCode(propertyTypeCode) === latestAction.type
        )
      ) {
        updateNearbyCondos(
          updateListingFavoritesQuery(latestAction, 'listings')
        );
      }
      if (
        nearbyNeigborsCondos?.listings?.data?.some(
          ({
            id,
            attributes: {
              propertyTypeCode,
              unit: {
                data: { id: unitId },
              },
            },
          }) =>
            latestAction.unitId === unitId &&
            getPropertyTypeByCode(propertyTypeCode) === latestAction.type
        )
      ) {
        updateNearbyNeigborsCondos(
          updateListingFavoritesQuery(latestAction, 'listings')
        );
      }
    }
  }, [latestAction]);

  const { nearbyCondos, nearbyCondosTotal } = useMemo(() => {
    return {
      nearbyCondos: (nearbyCondosData?.listings?.data || [])
        .concat(nearbyNeigborsCondos?.listings?.data || [])
        .slice(0, 12),
      nearbyCondosTotal:
        Number(nearbyCondosData?.listings?.meta?.pagination?.total || 0) +
        Number(nearbyNeigborsCondos?.listings?.meta?.pagination?.total || 0),
    };
  }, [nearbyCondosData, nearbyNeigborsCondos]);

  return (
    <UnitPageContext.Provider
      value={{
        nearbyCondos,
        nearbyCondosTotal,
        listing,
        listings,
        blogs,
        calculatedParams: {
          ...nearbyCondosParams,
          maxPrice: nearbyCondosParams.maxPriceSqft,
          minPrice: nearbyCondosParams.minPriceSqft,
        },
        building,
        unit,
        neighborhood,
        city,
        url: `${ORIGIN}${asPath}`,
        favorite,
        nearbyCondosLoading,
      }}
    >
      <UnitPage />
    </UnitPageContext.Provider>
  );
};

export default UnitComponent;

export const getServerSideProps: GetServerSideProps<
  UnitPageProps,
  { urlUnitParam: string; urlParam: string; urlBuildParam: string }
> = async ({ params, req }) => {
  const urlUnitParam = params?.urlUnitParam as string;
  const urlParam = params?.urlParam as string;
  const urlBuildParam = params?.urlBuildParam as string;
  const urlMatches = urlUnitParam.match(/unit-(.+)-for-(.+)/);
  // const URL = `/${urlParam}/${urlBuildParam}/${urlUnitParam}`;
  if (!urlMatches) return notfoundRedirect;

  const unique = urlMatches[1];
  const propertyType = urlMatches[2];

  let typeFilter: RequestPropertyType;
  if (propertyType === 'rent') {
    typeFilter = 'rentUnit';
  } else if (propertyType === 'sale') {
    typeFilter = 'saleUnit';
  } else {
    return notfoundRedirect;
  }

  const URL = `/${urlParam}/${urlBuildParam}/${urlUnitParam}`;
  const start = Date.now();
  console.log(`[UnitPage] getServerSideProps started for ${URL}`);

  const fetchListingsPage = (pageStart: number) =>
    queryWithRetry<{
      listings: {
        data: StrapiBulkResponseData<ListingPropertyType>;
        meta: { pagination: { total: number } };
      };
    }>(
      {
        query: GET_ALL_LISTINGS_FOR_UNIT_PAGE,
        variables: {
          unique,
          buldingSlug: urlBuildParam,
          nhSlug: urlParam,
          start: pageStart,
        },
      },
      'UnitPage/allListings'
    );

  try {
    const [unitResult, allListingsResult] = await Promise.allSettled([
      queryWithRetry<UnitPageUnitResponse>(
        {
          query: getUnitPageQuery(typeFilter),
          variables: {
            unique,
            buldingSlug: urlBuildParam,
            nhSlug: urlParam,
            blogsDateFrom: new Date(
              new Date().setFullYear(new Date().getFullYear() - 2)
            ).toISOString(),
          },
        },
        'UnitPage/unit'
      ),
      fetchListingsPage(0),
    ]);

    if (unitResult.status === 'rejected') {
      console.error(
        `[UnitPage] Unit query failed for ${URL}:`,
        unitResult.reason?.message || unitResult.reason
      );
      throw unitResult.reason;
    }

    if (allListingsResult.status === 'rejected') {
      console.error(
        `[UnitPage] AllListings query failed for ${URL}:`,
        allListingsResult.reason?.message || allListingsResult.reason
      );
      throw allListingsResult.reason;
    }

    const { listings, blogs } = unitResult.value.data;

    if (!listings.data.length) return notfoundRedirect;

    const listing = flattenStrapiDataItem(listings.data[0]);
    const unit = flattenStrapiDataItem(listing.unit?.data);

    if (!unit.id && unit.id !== 0) return notfoundRedirect;
    const buildingByUnit = flattenStrapiDataItem(unit.building?.data);
    if (!buildingByUnit.id && buildingByUnit.id !== 0) return notfoundRedirect;
    const neighborhood = flattenStrapiDataItem(
      buildingByUnit.neighborhood?.data
    );
    if (!neighborhood.id && neighborhood.id !== 0) return notfoundRedirect;

    const globalBlogs = flattenStrapiBulkDataItems(blogs?.data);
    const buildingBlogs = flattenStrapiBulkDataItems(
      buildingByUnit?.blogs.data
    );
    const neighborhoodBlogs = flattenStrapiBulkDataItems(
      neighborhood?.blogs.data
    );

    const pageBlogs = [
      ...buildingBlogs,
      ...neighborhoodBlogs,
      ...globalBlogs,
    ].slice(0, 3);

    const urlIsCorrect =
      urlBuildParam === buildingByUnit?.slug &&
      urlParam === neighborhood?.slug &&
      unique === unit.unique;

    if (!urlIsCorrect) return notfoundRedirect;

    const firstListingsPage = allListingsResult.value.data.listings;
    const allListings = await fetchRemainingPages(
      firstListingsPage.data,
      firstListingsPage.meta.pagination.total,
      UNIT_LISTINGS_PAGE_SIZE,
      (pageStart) =>
        fetchListingsPage(pageStart).then(({ data }) => data.listings.data)
    );

    console.log(`[UnitPage] getServerSideProps done in ${Date.now() - start}ms for ${URL}`);
    return {
      props: {
        blogs: pageBlogs,
        listing,
        listings: flattenStrapiBulkDataItems(allListings),
        neighborhood,
        unit,
        building: buildingByUnit,
        city: flattenStrapiDataItem(neighborhood.city.data),
        url: req.url,
      },
    };
  } catch (error: any) {
    console.error(`[UnitPage] Error fetching data for ${URL}:`);

    if (error?.graphQLErrors?.length) {
      error.graphQLErrors.forEach((gqlError: any, index: number) => {
        console.error(
          `  GraphQL Error ${index + 1}:`,
          JSON.stringify(
            {
              message: gqlError.message,
              extensions: gqlError.extensions,
              path: gqlError.path,
              locations: gqlError.locations,
            },
            null,
            2
          )
        );
      });
    }

    if (error?.networkError) {
      console.error(
        '  Network Error:',
        JSON.stringify(
          {
            message: error.networkError.message,
            statusCode: error.networkError.statusCode,
            result: error.networkError.result,
          },
          null,
          2
        )
      );
    }

    if (!error?.graphQLErrors?.length && !error?.networkError) {
      console.error('  Error:', error?.message || error);
    }

    return notfoundRedirect;
  }
};
